import React, { useState } from 'react'
import cx from 'classnames'
import { Text } from 'components/ui/Text'
import { useTranslations } from 'next-intl'

export interface OTPInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  size?: number
  error?: boolean
  onChange?: (value: string) => void
  errorMessage?: string
}

export const OTPInput = ({
  size = 6,
  error = false,
  onChange,
  errorMessage,
  ...props
}: OTPInputProps) => {
  const t = useTranslations()
  const ref = React.createRef<HTMLInputElement>()

  const [active, setActive] = useState(-1)
  const [value, setValue] = useState(props?.value?.toString() || '')

  const handleSquareClick = (index: number) => {
    if (ref.current instanceof HTMLInputElement) {
      setActive(index)
      ref.current.selectionStart = index
      ref.current.selectionEnd = index
      ref.current.focus()
    }
  }

  const handleCommonChange = (newData: string, isDeleted?: boolean) => {
    if (ref.current instanceof HTMLInputElement) {
      // If new data is empty or numberic input then update data, else reject new data and revoke with old data
      if (
        (newData?.length <= 0 || /^[0-9]*$/g.test(newData)) &&
        newData?.length <= size
      ) {
        const currentSelection = ref?.current?.selectionStart ?? 0
        const position = currentSelection <= size - 1 ? currentSelection : size
        const withDeletePostion = isDeleted ? position + 1 : position
        setValue(newData)
        setActive(withDeletePostion)
        ref.current.value = newData
        ref.current.selectionStart = withDeletePostion
        ref.current.selectionEnd = withDeletePostion

        if (newData?.length <= size) {
          onChange?.(newData)
        } else {
          onChange?.(newData?.slice(0, size))
        }
      } else {
        ref.current.value = value
      }
    }
  }

  const handleChangeValue = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const targetData = event?.target?.value
    // Good behaviour is check in the same active position at the first time
    if (targetData?.length < value?.length) {
      // When remove text, handle at old data
      const startBreak = active === value?.length ? active - 1 : active

      const newData = `${value?.slice(0, startBreak)}${value.slice(
        active + 1,
        value?.length,
      )}`

      handleCommonChange(newData, true)
    } else {
      // When replace or add data, handle at new data
      const newData = `${targetData?.slice(0, active + 1)}${targetData?.slice(
        active + 2,
        size + 1,
      )}`

      handleCommonChange(newData)
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = event.clipboardData.getData('text')
    handleCommonChange(pasteData)
    event.preventDefault()
  }

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.target instanceof HTMLInputElement) {
      const currentSelection = ref?.current?.selectionStart ?? 0
      const position = currentSelection <= size - 1 ? currentSelection : size
      setActive(position)
    }
  }

  return (
    <div className="relative space-y-2">
      <input
        {...props}
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        className="absolute opacity-0"
        onChange={handleChangeValue}
        onKeyUp={handleKeyUp}
        onPaste={handlePaste}
        onBlur={() => setActive(-1)}
      />
      <div className="w-full inline-flex justify-center space-x-2">
        {Array(size)
          .fill(null)
          .map((_, index) => {
            const text = value?.slice(index, index + 1) || ''
            // Verify text was only-one numberic text
            const view = /\b\d\b/g.test(text) ? text : ' '
            return (
              <button
                type="button"
                key={index}
                className="w-[46px] h-14 relative outline-none"
                onClick={() => handleSquareClick(index)}
              >
                <span
                  className={cx(
                    'w-full min-h-full rounded-xl border absolute top-0 left-0 flex items-center justify-center border-v2-gray-400 text-2xl font-semibold bg-white',
                    {
                      'border-v2-primary': index === active,
                      'border-v2-danger-200': !!error,
                    },
                  )}
                >
                  {view}
                </span>
              </button>
            )
          })}
      </div>
      {error && errorMessage ? (
        <Text
          fontSize="sm"
          v2TextColor="Da-Normal"
          className="text-center font-normal"
        >
          {errorMessage}
        </Text>
      ) : null}
      {error && !errorMessage ? (
        <Text
          fontSize="sm"
          v2TextColor="Da-Normal"
          className="text-center font-normal"
        >
          {t('incorrect_auth_code_message')}
        </Text>
      ) : null}
    </div>
  )
}

export default OTPInput
